/**
 * Provider component that handles CEISA WebSocket connection and displays notifications
 *
 * Usage: Wrap your dashboard layout with this provider
 *
 * Example:
 * ```tsx
 * <CeisaNotificationProvider ppjkNpwp={user.ppjkNpwp}>
 *   <YourDashboardContent />
 * </CeisaNotificationProvider>
 * ```
 */
import React, { useCallback, useMemo } from 'react';
import { useCeisaWebSocket } from '@hooks/useCeisaWebSocket';
import { useApprovalWebSocket } from '@hooks/approval/use-approval-websocket';
import { CeisaNotificationContainer } from './CeisaNotification';
import { usePpjkRealtimeInvalidation } from '@/features/realtime/ppjkEvents';

interface CeisaNotificationProviderProps {
  ppjkNpwp?: string;
  children: React.ReactNode;
}

export const CeisaNotificationProvider: React.FC<CeisaNotificationProviderProps> = ({
  ppjkNpwp,
  children,
}) => {
  const { visualNotifications: ceisaNotifs, removeVisualNotification: removeCeisa } = useCeisaWebSocket(ppjkNpwp);
  // Consignee reads stay current when anyone in the PPJK changes the master.
  usePpjkRealtimeInvalidation();
  const { visualNotifications: approvalNotifs, removeVisualNotification: removeApproval } = useApprovalWebSocket(ppjkNpwp);

  // Merge both notification streams
  const allNotifications = useMemo(
    () => [...ceisaNotifs, ...approvalNotifs],
    [ceisaNotifs, approvalNotifs],
  );

  const handleRemove = useCallback((id: string) => {
    removeCeisa(id);
    removeApproval(id);
  }, [removeCeisa, removeApproval]);

  // Check if demo user
  const isDemoUser = React.useMemo(() => {
    try {
      const authDataRaw = localStorage.getItem('authData');
      if (!authDataRaw) return false;

      const authData = JSON.parse(authDataRaw);
      const email = authData.email?.toLowerCase() || '';

      return email.includes('demo');
    } catch {
      return false;
    }
  }, []);

  // Log WebSocket status
  React.useEffect(() => {
    if (isDemoUser) {
      console.log('ℹ️ CeisaNotificationProvider: Demo user detected. WebSocket notifications are disabled for demo accounts.');
    } else if (!ppjkNpwp) {
      console.warn('⚠️ CeisaNotificationProvider: No PPJK NPWP provided. WebSocket notifications will not work. User needs PPJK NPWP in their profile.');
    }
  }, [ppjkNpwp, isDemoUser]);

  return (
    <>
      {children}

      {/* Notification container — renders both CEISA and approval popups */}
      <CeisaNotificationContainer
        notifications={allNotifications}
        onRemove={handleRemove}
      />
    </>
  );
};

export default CeisaNotificationProvider;
